<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { BookOpenCheck, Check, FileCheck2, Scale, ShieldCheck, TriangleAlert } from "@lucide/vue";

const stored = localStorage.getItem("wavecraft-rights-checks");
const checks = ref(
  stored
    ? (JSON.parse(stored) as boolean[])
    : [false, false, false, false, false],
);
const score = computed(() => Math.round((checks.value.filter(Boolean).length / checks.value.length) * 100));
watch(checks, (value) => localStorage.setItem("wavecraft-rights-checks", JSON.stringify(value)), { deep: true });

const rules = [
  "歌词是我原创、已获授权，或由模型根据我提供的主题生成",
  "没有要求模型模仿可识别的在世歌手、作曲家或特定受保护作品",
  "未上传未经授权的参考录音、人声、伴奏或采样",
  "我会按发行商与目标平台规则披露 AI 辅助创作",
  "正式发行前会保存提示词、歌词版本、母带和生成时间作为创作记录",
];
</script>

<template>
  <div class="rights-layout">
    <section class="panel rights-main">
      <div class="section-heading border-bottom">
        <div><p class="eyebrow">RIGHTS CHECKPOINT</p><h3>先把权利说清楚，再让作品走得更远</h3></div>
        <span class="readiness"><b>{{ score }}</b><small>合规自检</small></span>
      </div>
      <div class="rights-intro">
        <span><ShieldCheck :size="26" /></span>
        <div><strong>AI 生成不等于“自动拥有所有权”</strong><p>权利判断取决于输入素材、人工创作贡献、所在地法律、发行商条款与平台政策。这里提供工作流检查，不构成法律意见。</p></div>
      </div>
      <div class="rights-checklist">
        <label v-for="(rule, index) in rules" :key="rule" :class="{ done: checks[index] }">
          <input v-model="checks[index]" type="checkbox" />
          <span class="check-box"><Check :size="15" /></span>
          <span><small>0{{ index + 1 }}</small><strong>{{ rule }}</strong></span>
        </label>
      </div>
      <div class="rights-result" :class="{ ready: score === 100 }">
        <FileCheck2 :size="21" />
        <p><strong>{{ score === 100 ? "基础自检已完成" : "还有权利问题需要确认" }}</strong><span>{{ score === 100 ? "你可以进入发行中心整理作品元数据；提交前仍需复核发行商条款。" : "逐项核对，不确定的素材先不要提交发行。" }}</span></p>
        <router-link class="button button-dark button-sm" to="/release">前往发行中心</router-link>
      </div>
    </section>

    <aside class="rights-side">
      <article class="panel">
        <span class="outline-icon"><BookOpenCheck :size="19" /></span>
        <h3>建议保留的创作证据</h3>
        <ul class="plain-list"><li>生成提示词与模型版本</li><li>歌词草稿和人工修改记录</li><li>生成时间与原始母带</li><li>素材授权或购买凭证</li><li>发行商提交与审核记录</li></ul>
      </article>
      <article class="panel warning-card">
        <TriangleAlert :size="20" />
        <h3>高风险做法</h3>
        <p>使用艺人姓名要求“声音一模一样”、上传商业歌曲做未经授权翻唱、采样未获许可的录音，都可能导致下架或索赔。</p>
      </article>
      <article class="panel">
        <span class="outline-icon"><Scale :size="19" /></span>
        <h3>何时咨询专业人士</h3>
        <p class="muted-copy">当作品使用第三方素材、准备商业授权、收到侵权通知或跨地区发行时，建议咨询熟悉音乐版权的律师。</p>
      </article>
    </aside>
  </div>
</template>
